/**
 * Assinatura do grupo — o que a plataforma cobra do dono.
 *
 * Não confundir com o financeiro do grupo: aqui o dinheiro vai do dono para a
 * plataforma, e nada passa pelo caixa da pelada.
 */

import type { BillingState, PlatformInvoice, PlatformInvoiceStatus } from '@nossorachao/types';
import { formatCurrency, monthLabel } from '@nossorachao/utils';
import { AlertTriangle, Lock, QrCode, Receipt, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Badge, Button, Card, EmptyState, Field, Input, Skeleton } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  useBillingCharge,
  useBillingStatus,
  useGroupBilling,
  useSubmitBillingReceipt,
} from '@/lib/queries';

import { Modal } from './groups';

type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'info' | 'brand';

export const BILLING_STATE_LABEL: Record<BillingState, { label: string; tone: Tone }> = {
  DISABLED: { label: 'Sem cobrança', tone: 'neutral' },
  EXEMPT: { label: 'Isento', tone: 'info' },
  OK: { label: 'Em dia', tone: 'ok' },
  DUE: { label: 'A vencer', tone: 'brand' },
  IN_REVIEW: { label: 'Em conferência', tone: 'info' },
  OVERDUE: { label: 'Vencida', tone: 'warn' },
  BLOCKED: { label: 'Bloqueado', tone: 'bad' },
};

export const INVOICE_STATUS_LABEL: Record<PlatformInvoiceStatus, { label: string; tone: Tone }> = {
  PENDING: { label: 'Em aberto', tone: 'warn' },
  IN_REVIEW: { label: 'Em conferência', tone: 'info' },
  PAID: { label: 'Paga', tone: 'ok' },
  WAIVED: { label: 'Isenta', tone: 'neutral' },
  CANCELED: { label: 'Cancelada', tone: 'neutral' },
};

export const formatDay = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * Aviso no topo do grupo.
 *
 * Participante comum só vê o bloqueio — é o que explica por que os botões
 * falham. O atraso dentro da carência é assunto do dono e dos admins.
 */
export function BillingBanner({
  groupId,
  isAdmin,
  onOpen,
}: {
  groupId: string;
  isAdmin: boolean;
  onOpen: () => void;
}) {
  const { data } = useBillingStatus(groupId);
  if (!data) return null;

  const invoice = data.openInvoice;

  if (data.state === 'BLOCKED') {
    return (
      <Card className="mb-6" accent="rgb(217 45 32)">
        <div className="flex flex-wrap items-start gap-3">
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-bad" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Grupo somente leitura</p>
            <p className="mt-0.5 text-sm text-ink-2">
              {isAdmin
                ? `A assinatura${invoice ? ` de ${monthLabel(invoice.referenceMonth)}` : ''} venceu${
                    invoice ? ` em ${formatDay(invoice.dueDate)}` : ''
                  }. Dá para ver tudo, mas nada pode ser alterado até o pagamento.`
                : 'Dá para ver tudo, mas presenças, partidas e pagamentos estão travados. Avise o dono da pelada.'}
            </p>
          </div>
          {isAdmin ? (
            <Button variant="danger" size="sm" onClick={onOpen}>
              Regularizar
            </Button>
          ) : null}
        </div>
      </Card>
    );
  }

  if (isAdmin && data.state === 'OVERDUE') {
    return (
      <Card className="mb-6" accent="rgb(220 104 3)">
        <div className="flex flex-wrap items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warn" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Assinatura vencida</p>
            <p className="mt-0.5 text-sm text-ink-2">
              {invoice
                ? `${formatCurrency(invoice.amountCents)} · venceu em ${formatDay(invoice.dueDate)}. `
                : ''}
              {data.blocksAt
                ? `O grupo fica somente leitura em ${formatDay(data.blocksAt)}.`
                : null}
            </p>
          </div>
          <Button size="sm" onClick={onOpen}>
            Pagar
          </Button>
        </div>
      </Card>
    );
  }

  return null;
}

/** Aba "Assinatura": faturas, Pix e comprovante. Só para administração. */
export function GroupBilling({ groupId }: { groupId: string }) {
  const billing = useGroupBilling(groupId, true);
  const [paying, setPaying] = useState<PlatformInvoice | null>(null);

  if (billing.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!billing.data) {
    return (
      <Card>
        <p className="text-sm text-ink-2">Não foi possível carregar a assinatura.</p>
      </Card>
    );
  }

  const detail = billing.data;
  const state = BILLING_STATE_LABEL[detail.state];

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-ink-3">
              Assinatura do NossoRachão
            </p>
            <p className="tabular mt-1 text-2xl font-extrabold">
              {detail.exempt
                ? 'Isento'
                : detail.priceCents > 0
                  ? `${formatCurrency(detail.priceCents)}/mês`
                  : 'Sem cobrança'}
            </p>
            <p className="mt-1 text-[13px] text-ink-3">
              Pago pelo dono do grupo à plataforma — não sai do caixa da pelada.
              {detail.graceDays > 0 && !detail.exempt
                ? ` Depois do vencimento há ${detail.graceDays} dias de carência antes de o grupo ficar somente leitura.`
                : ''}
            </p>
          </div>
          <Badge tone={state.tone}>{state.label}</Badge>
        </div>
      </Card>

      {!detail.invoices.length ? (
        <Card>
          <EmptyState
            icon={Receipt}
            title="Nenhuma fatura"
            description="As faturas aparecem aqui no início de cada mês."
          />
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-line">
            {detail.invoices.map((invoice) => {
              const status = INVOICE_STATUS_LABEL[invoice.status];
              const open = invoice.status === 'PENDING' || invoice.status === 'IN_REVIEW';

              return (
                <li key={invoice.id} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold capitalize">{monthLabel(invoice.referenceMonth)}</p>
                    <p className="text-[13px] text-ink-3">
                      {formatCurrency(invoice.amountCents)} · vence {formatDay(invoice.dueDate)}
                      {invoice.paidAt ? ` · paga em ${formatDay(invoice.paidAt)}` : ''}
                    </p>
                    {invoice.status === 'PENDING' && invoice.statusReason ? (
                      <p className="mt-1 text-[13px] text-bad">
                        Comprovante recusado: {invoice.statusReason}
                      </p>
                    ) : null}
                    {invoice.status === 'IN_REVIEW' ? (
                      <p className="mt-1 text-[13px] text-ink-3">
                        Comprovante enviado — a plataforma confere e dá baixa.
                      </p>
                    ) : null}
                  </div>
                  <Badge tone={status.tone}>{status.label}</Badge>
                  {open ? (
                    <Button
                      size="sm"
                      variant={invoice.status === 'PENDING' ? 'primary' : 'secondary'}
                      icon={QrCode}
                      onClick={() => setPaying(invoice)}
                    >
                      {invoice.status === 'PENDING' ? 'Pagar' : 'Reenviar'}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {paying ? (
        <PayInvoiceModal
          groupId={groupId}
          invoice={paying}
          pixConfigured={detail.pixConfigured}
          onClose={() => setPaying(null)}
        />
      ) : null}
    </div>
  );
}

function PayInvoiceModal({
  groupId,
  invoice,
  pixConfigured,
  onClose,
}: {
  groupId: string;
  invoice: PlatformInvoice;
  pixConfigured: boolean;
  onClose: () => void;
}) {
  const charge = useBillingCharge(groupId);
  const submit = useSubmitBillingReceipt(groupId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);

  return (
    <Modal title={`Assinatura de ${monthLabel(invoice.referenceMonth)}`} onClose={onClose}>
      <div className="space-y-4 text-center">
        <p className="tabular text-3xl font-extrabold">{formatCurrency(invoice.amountCents)}</p>

        {!pixConfigured ? (
          <p className="text-[13px] text-ink-3">
            A chave Pix da plataforma ainda não foi configurada. Fale com o suporte para pagar.
          </p>
        ) : charge.data ? (
          <>
            <img
              src={charge.data.qrCodeImage}
              alt="QR Code do Pix"
              className="mx-auto h-56 w-56 rounded-lg bg-white p-2"
            />
            <Field label="Pix Copia e Cola">
              <Input readOnly value={charge.data.brCode} className="font-mono text-[11px]" />
            </Field>
            <Button
              full
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(charge.data.brCode);
                setCopied(true);
              }}
            >
              {copied ? 'Copiado!' : 'Copiar código'}
            </Button>
          </>
        ) : (
          <Button
            full
            icon={QrCode}
            loading={charge.isPending}
            onClick={() => charge.mutate(invoice.id)}
          >
            Gerar QR Code Pix
          </Button>
        )}

        {charge.error ? (
          <p className="text-[13px] text-bad">
            {charge.error instanceof ApiError ? charge.error.message : 'Erro ao gerar o Pix.'}
          </p>
        ) : null}

        <div className="border-t border-line pt-4">
          <p className="mb-3 text-[13px] text-ink-3">
            Depois de pagar, envie o comprovante. O grupo sai do bloqueio na hora, e a plataforma
            confere o recebimento em seguida.
          </p>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (!file) return;
              submit.mutate({ invoiceId: invoice.id, file }, { onSuccess: onClose });
            }}
          />
          <Button
            full
            icon={Upload}
            loading={submit.isPending}
            onClick={() => fileInput.current?.click()}
          >
            Já paguei — enviar comprovante
          </Button>
          {submit.error ? (
            <p className="mt-1.5 text-[12px] text-bad">
              {submit.error instanceof ApiError
                ? submit.error.message
                : 'Erro ao enviar comprovante.'}
            </p>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
